package org.chiangmeng.qfast.features.auth

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.chiangmeng.qfast.core.domain.ports.AuthRepository
import org.chiangmeng.qfast.core.domain.ports.QueueRepository

sealed interface AuthUiState {
    data object Idle : AuthUiState
    data object Loading : AuthUiState
    data object Success : AuthUiState
    data class Authenticated(val shopId: String) : AuthUiState
    data class Error(val message: String) : AuthUiState
    data class NeedShopName(val uid: String) : AuthUiState
}

class AuthViewModel(
    private val authRepository: AuthRepository,
    private val queueRepository: QueueRepository
) : ScreenModel {

    private val _uiState = MutableStateFlow<AuthUiState>(AuthUiState.Idle)
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    fun checkInitialAuth() {
        val currentUid = authRepository.getCurrentUserId()
        if (currentUid != null) {
            screenModelScope.launch {
                val hasShop = queueRepository.checkShopExists(currentUid)
                if (hasShop) {
                    _uiState.value = AuthUiState.Authenticated(currentUid)
                } else {
                    // สร้างร้านค้าเริ่มต้นอัตโนมัติ
                    try {
                        queueRepository.createNewShop(currentUid, "จุดบริการคิว")
                        _uiState.value = AuthUiState.Authenticated(currentUid)
                    } catch (e: Throwable) {
                        _uiState.value = AuthUiState.Error("บันทึกข้อมูลเริ่มต้นไม่สำเร็จ กรุณาลองใหม่อีกครั้ง")
                    }
                }
            }
        }
    }

    fun signOut() {
        screenModelScope.launch {
            authRepository.signOut()
            _uiState.value = AuthUiState.Idle
        }
    }

    // รีเซ็ตสถานะ Error
    fun clearError() {
        _uiState.value = AuthUiState.Idle
    }

    fun setManualError(message: String) {
        _uiState.value = AuthUiState.Error(message)
    }

    fun signInWithGoogle(idToken: String, accessToken: String? = null) {
        screenModelScope.launch {
            _uiState.value = AuthUiState.Loading
            authRepository.signInWithGoogle(idToken, accessToken)
                .onSuccess { uid ->
                    val shopExists = queueRepository.checkShopExists(uid)
                    if (shopExists) {
                        _uiState.value = AuthUiState.Authenticated(uid)
                    } else {
                        // สร้างร้านค้าเริ่มต้นอัตโนมัติ
                        try {
                            queueRepository.createNewShop(uid, "จุดบริการคิว")
                            _uiState.value = AuthUiState.Authenticated(uid)
                        } catch (e: Throwable) {
                            println("QFAST_AUTH_DEBUG: createNewShop failed -> ${e.message}")
                            _uiState.value = AuthUiState.Error("สร้างข้อมูลเริ่มต้นไม่สำเร็จ กรุณาลองใหม่อีกครั้ง")
                        }
                    }
                }
                .onFailure { t ->
                    println("QFAST_AUTH_DEBUG: signInWithGoogle failed -> ${t.message}")
                    // ส่งข้อความ Error จริงเข้า State โดยตรง
                    _uiState.value = AuthUiState.Error(t.message ?: "signInWithGoogle failed: Unknown error")
                }
        }
    }

    fun signInWithApple(idToken: String, rawNonce: String) {
        screenModelScope.launch {
            _uiState.value = AuthUiState.Loading
            authRepository.signInWithApple(idToken, rawNonce).fold(
                onSuccess = { uid ->
                    val hasShop = queueRepository.checkShopExists(uid)
                    if (hasShop) {
                        _uiState.value = AuthUiState.Authenticated(uid)
                    } else {
                        // สร้างร้านค้าเริ่มต้นอัตโนมัติ
                        try {
                            queueRepository.createNewShop(uid, "จุดบริการคิว")
                            _uiState.value = AuthUiState.Authenticated(uid)
                        } catch (e: Throwable) {
                            println("QFAST_AUTH_DEBUG: createNewShop failed -> ${e.message}")
                            _uiState.value = AuthUiState.Error("สร้างข้อมูลเริ่มต้นไม่สำเร็จ กรุณาลองใหม่อีกครั้ง")
                        }
                    }
                },
                onFailure = { e ->
                    println("QFAST_AUTH_DEBUG: signInWithApple failed -> ${e.message}")
                    _uiState.value = AuthUiState.Error(e.message ?: "Apple Sign-In failed")
                }
            )
        }
    }

    fun completeSocialSignUp(uid: String, shopName: String) {
        if (shopName.isBlank()) {
            _uiState.value = AuthUiState.Error("กรุณากรอกชื่อร้านค้า")
            return
        }
        screenModelScope.launch {
            _uiState.value = AuthUiState.Loading
            try {
                queueRepository.createNewShop(uid, shopName.trim())
                _uiState.value = AuthUiState.Authenticated(uid)
            } catch (t: Throwable) {
                println("QFAST_AUTH_DEBUG: createNewShop failed -> ${t.message}")
                _uiState.value = AuthUiState.Error("สร้างร้านค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง")
            }
        }
    }

    fun cancelSocialSignUp() {
        screenModelScope.launch {
            authRepository.signOut()
            _uiState.value = AuthUiState.Idle
        }
    }

    private fun mapFirebaseError(rawMessage: String?): String {
        val msg = rawMessage?.lowercase() ?: return "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง"
        return when {
            msg.contains("invalid-credential") || msg.contains("invalid_login_credentials") ||
            msg.contains("invalid credential") || msg.contains("wrong-password") ||
            msg.contains("wrong_password") || msg.contains("user-not-found") ||
            msg.contains("user_not_found") || msg.contains("no user record") ->
                "อีเมลหรือรหัสผ่านไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง"
            msg.contains("email-already-in-use") || msg.contains("already in use") ->
                "อีเมลนี้มีบัญชีในระบบแล้ว กรุณาเข้าสู่ระบบ"
            msg.contains("invalid-email") || msg.contains("badly formatted") ->
                "รูปแบบอีเมลไม่ถูกต้อง กรุณาตรวจสอบอีเมลของคุณ"
            msg.contains("weak-password") || msg.contains("at least 6 characters") ->
                "รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร"
            msg.contains("too-many-requests") || msg.contains("unusual activity") ->
                "ลองเข้าสู่ระบบผิดพลาดบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่"
            msg.contains("network-request-failed") || msg.contains("network error") || msg.contains("unable to resolve host") ->
                "ไม่สามารถเชื่อมต่ออินเทอร์เน็ตได้ กรุณาตรวจสอบสัญญาณของคุณ"
            else ->
                "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง"
        }
    }
}
